import { useState, useCallback, useRef } from 'react';
import type {
  ScreenerState,
  PendingSlot,
  ActiveScreener,
  ScreenerCompletePayload,
  ScreenerResultPayload,
  CrisisRouteData,
  SlotSelectedPayload,
  AppointmentBookedPayload,
  ScreenerGateConfig,
  AcuitySeverity,
} from './screenerTypes';
import type { EmbeddedToHostMessage, ScheduleEvent } from '../types';

export interface ScreenerGateHandle {
  // Reactive state (for rendering)
  screenerState: ScreenerState;
  pendingSlot: PendingSlot | null;
  activeScreener: ActiveScreener | null;
  crisisData: CrisisRouteData | null;
  // Stable refs (safe to read from stale closures like message handlers)
  pendingSlotRef: React.MutableRefObject<PendingSlot | null>;
  lastCompletedPayloadRef: React.MutableRefObject<ScreenerCompletePayload | null>;
  // Transitions
  handleSlotIntercepted: (slot: PendingSlot) => void;
  onConfigReceived: (screener: ActiveScreener) => void;
  onSkip: () => void;
  onScreenerCompleted: (responses: { questionId: string; value: number | string }[]) => ScreenerCompletePayload | null;
  onResultReceived: (result: ScreenerResultPayload) => void;
  onCrisisAcknowledged: () => void;
  reset: () => void;
}

export function useScreenerGate(
  screenerGate: ScreenerGateConfig | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  createEvent: (event: Omit<ScheduleEvent, 'id'>) => void,
): ScreenerGateHandle {
  const [screenerState, setScreenerState] = useState<ScreenerState>('IDLE');
  const [pendingSlot, setPendingSlot] = useState<PendingSlot | null>(null);
  const [activeScreener, setActiveScreener] = useState<ActiveScreener | null>(null);
  const [crisisData, setCrisisData] = useState<CrisisRouteData | null>(null);

  // Refs for reading current values from within stale message-handler closures.
  const pendingSlotRef = useRef<PendingSlot | null>(null);
  const lastCompletedPayloadRef = useRef<ScreenerCompletePayload | null>(null);

  const reset = useCallback(() => {
    setScreenerState('IDLE');
    setPendingSlot(null);
    pendingSlotRef.current = null;
    setActiveScreener(null);
    setCrisisData(null);
    lastCompletedPayloadRef.current = null;
  }, []);

  // Emit APPOINTMENT_BOOKED, create the calendar event, and reset.
  const concludeBooking = useCallback(
    (
      result: ScreenerResultPayload & { action: 'CONFIRM' | 'ADJUST_SLOT' },
      completed: ScreenerCompletePayload,
      slot: PendingSlot,
    ) => {
      const resolvedResourceId =
        result.action === 'ADJUST_SLOT' && result.preferredResourceId
          ? result.preferredResourceId
          : slot.resourceId;

      const appointmentId =
        result.action === 'CONFIRM'
          ? result.appointmentId
          : slot.slotId;

      const payload: AppointmentBookedPayload = {
        appointmentId,
        slotId: slot.slotId,
        resourceId: resolvedResourceId,
        patientId: slot.patientId,
        screener: completed.screener,
        severity: completed.severity,
        ...(result.action === 'ADJUST_SLOT' && result.adjustedDuration !== undefined
          ? { adjustedDuration: result.adjustedDuration }
          : {}),
      };

      emit({ type: 'APPOINTMENT_BOOKED', payload });

      // Place the event on the calendar. For ADJUST_SLOT the end time is recomputed
      // from the start using the host-supplied duration; otherwise the original slot
      // end is used as-is.
      let endUtc = slot.end;
      if (result.action === 'ADJUST_SLOT' && result.adjustedDuration != null) {
        const adjusted = new Date(slot.start);
        adjusted.setMinutes(adjusted.getMinutes() + result.adjustedDuration);
        endUtc = adjusted.toISOString();
      }

      createEvent({
        title: 'Appointment',
        startUtc: slot.start,
        endUtc,
        resourceId: resolvedResourceId || undefined,
        screenerSeverity: completed.severity === 'crisis' ? undefined : completed.severity,
      });

      reset();
    },
    [emit, createEvent, reset],
  );

  // Route a SCREENER_RESULT payload to the right outcome.
  const applyResult = useCallback(
    (result: ScreenerResultPayload, completed: ScreenerCompletePayload, slot: PendingSlot) => {
      if (result.action === 'CRISIS_ROUTE') {
        setCrisisData({ message: result.message, resources: result.resources });
        setScreenerState('CRISIS_PANEL');
        return;
      }
      concludeBooking(result, completed, slot);
    },
    [concludeBooking],
  );

  // ── Transitions ────────────────────────────────────────────────────────────

  const handleSlotIntercepted = useCallback(
    (slot: PendingSlot) => {
      setPendingSlot(slot);
      pendingSlotRef.current = slot;
      setScreenerState('AWAITING_CONFIG');

      const payload: SlotSelectedPayload = {
        slotId: slot.slotId,
        resourceId: slot.resourceId,
        start: slot.start,
        end: slot.end,
        patientId: slot.patientId,
      };

      emit({ type: 'SLOT_SELECTED', payload });

      // Direct-React mode: callback may return a Promise with the screener config.
      if (screenerGate?.onSlotSelected) {
        const maybePromise = screenerGate.onSlotSelected(payload);
        if (maybePromise && typeof (maybePromise as Promise<unknown>).then === 'function') {
          (maybePromise as Promise<{ screener: ActiveScreener } | 'skip'>).then((response) => {
            if (response === 'skip') {
              reset();
            } else if (response?.screener) {
              setActiveScreener(response.screener);
              setScreenerState('SCREENER_OPEN');
            }
          });
        }
      }
    },
    [screenerGate, emit, reset],
  );

  const onConfigReceived = useCallback((screener: ActiveScreener) => {
    setActiveScreener(screener);
    setScreenerState('SCREENER_OPEN');
  }, []);

  const onSkip = useCallback(() => {
    reset();
  }, [reset]);

  const onScreenerCompleted = useCallback(
    (responses: { questionId: string; value: number | string }[]): ScreenerCompletePayload | null => {
      const slot = pendingSlotRef.current;
      const screener = activeScreener;
      if (!slot || !screener) return null;

      // Compute preliminary score from numeric/likert values; null for text-only screeners.
      const numericValues = responses
        .map((r) => r.value)
        .filter((v): v is number => typeof v === 'number');
      const score = numericValues.length > 0 ? numericValues.reduce((a, b) => a + b, 0) : null;

      const t = screener.acuityThresholds;
      let severity: AcuitySeverity;
      if (score === null) {
        severity = 'low'; // text-only: conservative default; host decides
      } else if (score <= t.low) {
        severity = 'low';
      } else if (score <= t.moderate) {
        severity = 'moderate';
      } else if (score <= t.high) {
        severity = 'high';
      } else {
        severity = 'crisis';
      }

      const completed: ScreenerCompletePayload = {
        slotId: slot.slotId,
        resourceId: slot.resourceId,
        start: slot.start,
        end: slot.end,
        patientId: slot.patientId,
        screener: screener.id,
        responses,
        score,
        severity,
      };

      lastCompletedPayloadRef.current = completed;
      setScreenerState('AWAITING_RESULT');

      // Direct-React mode: callback may return a Promise with the routing decision.
      if (screenerGate?.onScreenerComplete) {
        const maybePromise = screenerGate.onScreenerComplete(completed);
        if (maybePromise && typeof (maybePromise as Promise<unknown>).then === 'function') {
          (maybePromise as Promise<ScreenerResultPayload>).then((result) => {
            applyResult(result, completed, slot);
          });
        }
      }

      return completed;
    },
    [activeScreener, screenerGate, applyResult],
  );

  // Called from the context's postMessage handler for iframe mode.
  const onResultReceived = useCallback(
    (result: ScreenerResultPayload) => {
      const slot = pendingSlotRef.current;
      const completed = lastCompletedPayloadRef.current;
      if (!slot || !completed) {
        reset();
        return;
      }
      applyResult(result, completed, slot);
    },
    [applyResult, reset],
  );

  const onCrisisAcknowledged = useCallback(() => {
    reset();
  }, [reset]);

  return {
    screenerState,
    pendingSlot,
    activeScreener,
    crisisData,
    pendingSlotRef,
    lastCompletedPayloadRef,
    handleSlotIntercepted,
    onConfigReceived,
    onSkip,
    onScreenerCompleted,
    onResultReceived,
    onCrisisAcknowledged,
    reset,
  };
}
